import { AdBreak, AdUnit, isAdBreak } from "../../ads/AdUnit.js";
import { AdErrorStruct, PlayerAdEvent, PlayerAdEventType, isPlayerAdBreakRelatedEvent, isPlayerAdEvent, isPlayerAdUnitRelatedEvent } from "../../ads/PlayerAdEvent.js";
import { MediaEvent, MediaProgressEvent, PlayerEvent, isMediaCompleteEvent, isMediaEvent, isMediaImpressionEvent, isMediaProgressEvent, isMediaStartedEvent } from "../../events/PlayerEvent.js";
import { CommonEventMapper } from "./CommonEventMapper.js";
import { VolatileExecutionData } from "./VolatileExecutionData.js";
import { MediaInfoRawData } from "../../../model/MediaInfo.js";

export interface PreFilteredEventMapperConfig {

    media: boolean,
    aderrors?: boolean,
    adevents?: PlayerAdEventType[],
    progressMilestones?: number[]
}


export const DEFAULT_PROGRESS_MILESTONES = [25,50,75,90];

function evtTypeToLabel(tp: PlayerAdEventType): string {
    switch(tp) {
        case PlayerAdEventType.AD_REQUESTED:
            return "requested";
        case PlayerAdEventType.AD_CLICKED:
            return "clicked";
        case PlayerAdEventType.AD_VIEWABILTY:
            return "viewable";
        case PlayerAdEventType.AD_USER_CLOSE:
            return "closed";
        case PlayerAdEventType.AD_STARTED:
            return "impression";
        case PlayerAdEventType.AD_PLAY_ERROR:
            return "play_error";
        case PlayerAdEventType.AD_LOAD_ERROR:
            return "load_error";
        case PlayerAdEventType.AD_COMPLETED:
            return "ended";
        case PlayerAdEventType.AD_LOADED:
            return "loaded";
        case PlayerAdEventType.AD_RESUMABLE_MEDIA:
            return "ad_resumable_media";
        case PlayerAdEventType.AD_PAUSED_MEDIA:
            return "ad_paused_media";
    }
}


const ADERRORS_TO_TRIGGER: {[k in PlayerAdEventType]?: boolean} = {
    [PlayerAdEventType.AD_LOAD_ERROR]: true,
    [PlayerAdEventType.AD_PLAY_ERROR]: true
}

export const DEFAULT_AD_EVENTS_TO_TRIGGER: PlayerAdEventType[] = 
    [
        PlayerAdEventType.AD_REQUESTED,
        PlayerAdEventType.AD_COMPLETED,
        PlayerAdEventType.AD_STARTED,
        PlayerAdEventType.AD_USER_CLOSE
    ];

/**
 * Abstract class for mapping and filtering player events before further processing.
 * 
 * `PreFilteredEventMapper` provides a base for handling ad-related and media-related events,
 * filtering them according to configuration, and triggering appropriate callbacks for
 * ad errors, ad events, media progress milestones, and general media events.
 * 
 * @template MI - The type of media info raw data.
 * 
 * @extends CommonEventMapper
 * 
 * @remarks
 * - Ad events and errors are filtered based on the provided configuration.
 * - Media progress events are triggered at specified milestones.
 * - Subclasses must implement handlers for ad errors, ad events, media progress, and media events.
 * 
 * @param cfg - Configuration object specifying which events and errors to handle.
 * 
 * @protected
 * @method extractEventLabel - Extracts a label for an ad event, using either the ad unit or ad break.
 * @method reactToEvents - Main event dispatcher, filters and routes events to appropriate handlers.
 * 
 * @abstract
 * @method onAdError - Handler for ad error events.
 * @method onAdEvent - Handler for ad events.
 * @method onMediaProgress - Handler for media progress milestone events.
 * @method onMediaEvent - Handler for general media events.
 */
export abstract class PreFilteredEventMapper<MI extends MediaInfoRawData<any>> extends CommonEventMapper {


    private enableAdEvents: boolean;
    private enableProgressEvents: boolean;
    private ad_events_to_trigger: {[k in PlayerAdEventType]?: boolean};
    private volatile: VolatileExecutionData;


    constructor(private cfg: PreFilteredEventMapperConfig) {
        super();
        if(this.cfg.adevents?.length > 0) {
            this.enableAdEvents = true;
            this.ad_events_to_trigger = {};
            this.cfg.adevents.forEach(adevt => {
                this.ad_events_to_trigger[adevt] = true;
            })
        }
        else {
            this.enableAdEvents = false;
            this.ad_events_to_trigger = null;
        }
        this.enableProgressEvents = this.cfg.progressMilestones?.length > 0;
        this.volatile = new VolatileExecutionData(this.enableProgressEvents?[25,50,75,90]:[])

    }

    protected extractEventLabel(adUnit: AdUnit, adBreak?: AdBreak): string {
        return isAdBreak(adUnit)?adUnit.label:adBreak.label;
    }

    protected reactToEvents(evt: PlayerEvent): void {
        try {
            if(isPlayerAdEvent(evt) && (this.cfg.aderrors || this.enableAdEvents)) {
                
                const adUnit: AdUnit      = isPlayerAdUnitRelatedEvent(evt)?evt.adUnit:null;
                const maybeBreak: AdBreak = isPlayerAdBreakRelatedEvent(evt)?evt.adBreak:null;


                if(adUnit) {
                    const prelabel = this.extractEventLabel(adUnit,maybeBreak);
                    const alab = evtTypeToLabel(evt.eventType);
                    
                    if(this.cfg.adevents && this.ad_events_to_trigger && evt.eventType in this.ad_events_to_trigger &&
                        this.ad_events_to_trigger[evt.eventType]) {
                            this.onAdEvent(evt,prelabel,alab);
                    }
                    else if (this.cfg.aderrors && evt.eventType in ADERRORS_TO_TRIGGER
                            && ADERRORS_TO_TRIGGER[evt.eventType]) {
                        if(isPlayerAdUnitRelatedEvent(evt) && evt.error && evt.errorData) {
                            this.onAdError(evt.errorData,prelabel,alab);
                        }
                    }

                }

            }
            else if (isMediaEvent(evt) && this.cfg.media) {
                const executionData = this.volatile.getExecutionInfo(evt.executionId);
                if(isMediaProgressEvent(evt)) {
                    if(this.enableProgressEvents) {
                        const milestone = executionData.extractProgressMilestone(evt.progress);
                        if(milestone >= 0) {
                            this.onMediaProgress(evt,milestone);
                        }
                    }
                }
                else {
                    if(isMediaStartedEvent(evt)) {
                        if(!executionData.started) {
                            executionData.setStarted();
                            this.onMediaEvent(evt);
                        }
                    }
                    else if(isMediaImpressionEvent(evt)) {
                        if(!executionData.impression) {
                            executionData.setImpression();
                            this.onMediaEvent(evt);
                        }
                    }
                    else if (isMediaCompleteEvent(evt)) {
                        if(!executionData.completed) {
                            executionData.setCompleted();
                            this.onMediaEvent(evt);
                        }
                    }
                    else {
                        this.onMediaEvent(evt);
                    }
                }

            }
        }catch(e) {
            console.error("[STATS-ERROR]",e);
        }
    }


    protected abstract onAdError(errorStruct: AdErrorStruct, adjustedLabel?: string,adjustedEventLabel?: string): void;
    protected abstract onAdEvent(evt: PlayerAdEvent, adjustedLabel?: string, adjustedEventLabel?: string): void;
    protected abstract onMediaProgress(progEvent: MediaProgressEvent, corrected_percentage: number): void;
    protected abstract onMediaEvent(evt: MediaEvent<MI>): void;



}